/**
 * AdminImportWO — Standalone paste-in page at /admin/import-wo so users
 * can land here from any environment (e.g. arrive on production after a
 * fresh redeploy and paste the bundle they exported from preview).
 */
import { useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Sparkle, CheckCircle, ArrowRight } from "@phosphor-icons/react";

export default function AdminImportWO() {
  const [pasted, setPasted]   = useState("");
  const [replace, setReplace] = useState(true);
  const [busy, setBusy]       = useState(false);
  const [result, setResult]   = useState(null);

  const onFile = async (file) => {
    if (!file) return;
    try { setPasted(await file.text()); }
    catch { toast.error("Couldn't read file"); }
  };

  const doImport = async () => {
    let payload;
    try { payload = JSON.parse(pasted); }
    catch { toast.error("Invalid JSON"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/import-wo-bundle", {
        bundle: payload, replace_if_exists: replace,
      });
      setResult(data);
      toast.success("Bundle imported");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Import failed")); }
    finally { setBusy(false); }
  };

  return (
    <AdminShell>
      <div className="max-w-3xl mx-auto p-4 space-y-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-gradient-to-br from-teal-600 to-emerald-700 text-white">
            <Upload weight="fill" size={26}/>
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-900">Import WO Bundle</h1>
            <p className="text-sm text-slate-500">Paste the JSON from your other environment (Preview ↔ Production).</p>
          </div>
        </div>

        <div className="rounded-2xl bg-emerald-50 border-2 border-emerald-200 p-4 text-sm text-emerald-900 flex items-start gap-2">
          <Sparkle weight="fill" size={16} className="text-emerald-600 mt-0.5"/>
          <div>
            <b>How it works:</b> On the source env, open any WO → Copy WO Folder → Export & Download.
            Bring that JSON here → paste or upload → Import. The full folder (job + quotes + change orders + album)
            is recreated instantly with fresh tokens. Photos aren't copied — upload them here as work progresses.
          </div>
        </div>

        <div className="rounded-2xl bg-white border-2 border-slate-200 p-4 space-y-3">
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Upload .json (optional)</label>
            <input type="file" accept="application/json,.json"
              onChange={e => onFile(e.target.files?.[0])}
              className="mt-1 w-full text-sm" data-testid="import-wo-file"/>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Or paste JSON</label>
            <textarea value={pasted} onChange={e => setPasted(e.target.value)}
              rows={14} placeholder='{"bundle_version":1,"job":...}'
              className="mt-1 w-full p-2 border-2 border-slate-300 text-xs font-mono outline-none"
              data-testid="import-wo-paste"/>
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-700">
            <input type="checkbox" checked={replace} onChange={e => setReplace(e.target.checked)}/>
            Overwrite child docs if the WO number already exists
          </label>
          <button onClick={doImport} disabled={busy || !pasted.trim()}
            data-testid="import-wo-btn"
            className="w-full h-12 bg-gradient-to-r from-emerald-600 to-teal-700 text-white font-black uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2 shadow-[3px_3px_0_#0f172a] disabled:opacity-50">
            <Upload weight="fill" size={16}/> {busy ? "Importing…" : "Import Bundle"}
          </button>
          {result && (
            <div className="rounded-md bg-emerald-50 border-2 border-emerald-300 p-3 text-[13px] text-emerald-900 flex items-start gap-2">
              <CheckCircle weight="fill" size={16} className="mt-0.5"/>
              <div className="flex-1">
                <div className="font-black">Success</div>
                <div>WO {result.work_order_number} — {result.was_existing ? "updated" : "created"} · {result.quotes_imported} quote(s) · {result.change_orders_imported} change order(s){result.album_token ? " · album minted" : ""}</div>
                <Link to={`/admin/jobs/${result.job_id}`} className="inline-flex items-center gap-1 mt-1 text-emerald-700 hover:text-emerald-900 font-bold text-xs">
                  Open the WO <ArrowRight size={12}/>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  );
}
